import { makeAutoObservable, runInAction } from "mobx";

import { transactionAjaxController } from "../../../Common/Ajax/TransactionAjaxController";
import { StockistDTO } from "../../../Common/DTOs/StockistDTO";
import { userAccountController } from "../../../Common/Controllers/UserAccountController";
import { APIUserDTO } from "../../../Common/DTOs/APIUserDTO";
import { userAjaxController } from "../../../Common/Ajax/UserAjaxController";
import { TransactionDTO } from "../../../Common/DTOs/Payment/TransactionDTO";
import { utils } from "../../../Common/Controllers/Utils";
import { basketController } from "../../../Common/Controllers/BasketController";
import { stockiestPortalController } from "../../../Common/Controllers/StockiestPortalController";
import { AddressDTO } from "../../../Common/DTOs/Address/AddressDTO";
import { addressesController } from "../../../Common/Controllers/AddressesController";
import { orderController } from "./OrderController";
import { StripePaymentResponse } from "../../../Common/DTOs/Payment/StripePaymentResponse";
import { alertController } from "../../../Common/Controllers/AlertController";


export class StripeCardController {
  private _isLoading = false;
  private _initialized = false;

  transactionId: number | null = null;

  private portalController = stockiestPortalController

  private _stockist: StockistDTO | null = null
  private _stockistBillingAddress: AddressDTO | null = null
  private _userIp: string | null = null

  private _paymentIntent: StripePaymentResponse | null = null;

  get isLoading() {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    this._isLoading = value;
  }

  get isInitialized() {
    return this._initialized && !!this._paymentIntent && !!this.transactionId && !!this._stockistBillingAddress && !!this._userIp;
  }

  getStockist = async () => {
    if ((!this._stockist || userAccountController.currentUser!.id != this._stockist.stockistId)
      && userAccountController.currentUser instanceof APIUserDTO) {
      this._stockist = await userAjaxController.getStockist(userAccountController.currentUser.id)
    }
    return this._stockist
  }

  private async getOrCreatePaymentIntent(transactionId: number): Promise<StripePaymentResponse> {
    if (
      !this._paymentIntent
    ) {
      const paymentResponse = await transactionAjaxController.getStripePaymentIntent(transactionId);
      runInAction(() => {
        this._paymentIntent = paymentResponse.paymentIntentResp;
      });
    }
    return this._paymentIntent!;
  }

  getOrCreateTransaction = async (stockistUserId: number) => {
    const transaction = await transactionAjaxController.getOrCreateTransaction(stockistUserId);
    runInAction(() => {
      this.transactionId = transaction.id;
    })

    return transaction;
  }

  get clientSecret(){
    return this._paymentIntent!.clientSecret;
  }

  createOrder = async () => {
    const orderCreationResponse = await transactionAjaxController.createOrder({
      transactionId: this.transactionId!,
      cardAmount: +this.portalController.totalPriceInclDiscountInPoundsWithDelivery.toFixed(2),
      type: 0, // order
      courier: orderController.selectedQuote!.courier,
      serviceType: orderController.selectedQuote!.serviceType,
      eori: userAccountController.stockist!.eori,
      declaredDelivery: orderController.selectedQuote!.deliveryDate,
      saturdayDelivery: orderController.selectedQuote!.saturdayDelivery,
      totalDeliveryCost: orderController.selectedQuote!.totalCost,
      costWithoutVat: orderController.selectedQuote!.costWithoutTaxes,
      vat: +orderController.selectedQuote!.taxes.toFixed(2),
      publisherPrice: orderController.selectedQuote!.costToPublisher
    })

    if (typeof orderCreationResponse !== 'string') {
      alertController.displayAlert(
        "Payment failed",
        "Payment request is not valid",
        "error",
        4000
      )

      return false;
    }

    return true;
  }

  isOrderValid = async () => {
    const orderValidation = await transactionAjaxController.validateOrderCreation({
      transactionId: this.transactionId!,
      cardAmount: +this.portalController.totalPriceInclDiscountInPoundsWithDelivery.toFixed(2),
      type: 0, // order
      courier: orderController.selectedQuote!.courier,
      serviceType: orderController.selectedQuote!.serviceType,
      eori: userAccountController.stockist!.eori,
      declaredDelivery: orderController.selectedQuote!.deliveryDate,
      saturdayDelivery: orderController.selectedQuote!.saturdayDelivery,
      totalDeliveryCost: orderController.selectedQuote!.totalCost,
      costWithoutVat: orderController.selectedQuote!.costWithoutTaxes,
      vat: +orderController.selectedQuote!.taxes.toFixed(2),
      publisherPrice: orderController.selectedQuote!.costToPublisher
    })

    if (!orderValidation.isValid) {
      alertController.displayAlert(
        "Payment failed",
        orderValidation.errors.map(e => e.errorMessage),
        "error",
        4000
      )

      return false;
    }

    return true;
  }

  startPayment = async () => {
    await transactionAjaxController.startPayment(this._paymentIntent!.id!)
  }

  finalizePayment = async () => {
    runInAction(() => this._paymentIntent = null)

    window.location.href = `/checkout/step-4/${this.transactionId}`

    runInAction(() => this.transactionId = null)
  }

  async init() {
    if (this._initialized) return;
    this._initialized = true;

    await this.initializePayment();
  }

  private initializePayment = async () => {
    this.isLoading = true;
    // user might not be available yet, retry if that is the case
    if (userAccountController.currentUser == null || basketController.basket == null) {
      await utils.callbackIn(200, this.initializePayment)
      return
    }

    await this.getStockist().then(async (stockist) => {
      if (!stockist) return

      const transaction = await this.getOrCreateTransaction(stockist?.stockistId)
      const orderCreated = await this.createOrder();
      if (!orderCreated) {
        this.isLoading = false;
        return;
      }

      await this.getOrCreatePaymentIntent(transaction!.id)
      await this.getAddress()
      await this.getUserIp()
      this.isLoading = false;
    })
  }

  getAddress = () => {
    this._stockistBillingAddress = addressesController.billingAddress
  }

  getUserIp = async () => {
    if (!this._userIp) {
      const userIp = await userAjaxController.getUserIp();
      runInAction(() => {
        this._userIp = userIp;
      })
    }
  }

  constructor() {
    makeAutoObservable(this);
  }
}